import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useClient } from '../context/ClientContext';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { getTopbarClientsCache, setTopbarClientsCache } from '../services/clientApi';
import {
  setTourDemoActive,
  isTourDemoActive,
  buildDemoClient,
  getDemoProviderId,
  DEMO_CLIENT_ID,
} from '../services/tourDemo';
import TourOverlay from './tour/TourOverlay';
import { getMascotVideoCandidates } from './tour/mascotAssets';
import { useAdminDashboardReady } from './tour/useAdminDashboardReady';
import { usePortfolioPlaybook } from './tour/usePortfolioPlaybook';
import {
  DASHBOARD_PATH,
  CLIENT_DASHBOARD_PATH,
  buildSteps,
  buildWelcome,
  readShowAssignments,
} from './tour/portfolioPlaybookSteps';
import { findTourElement, isMobileViewport } from './tour/tourElementUtils';

const COMPLETION_KEY = 'portfolioPlaybookCompletedV2';
const CLIENT_KEY = 'portfolioPlaybookClientId';

const PortfolioPlaybookTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { isAuthenticated } = useAuth();
  const { selectedClient, setSelectedClient } = useClient();
  const { currentUser } = useCurrentUser();

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const [hasClients, setHasClients] = useState(() => (getTopbarClientsCache() || []).length > 0);
  const [assignmentsFlag, setAssignmentsFlag] = useState(false);

  const canSeeCompany = ['company_manager', 'sys_admin'].includes(currentUser?.role);
  const showAssignments = assignmentsFlag || readShowAssignments(canSeeCompany);

  useEffect(() => {
    const syncLanguage = () => setLanguage(localStorage.getItem('language') || 'english');
    window.addEventListener('languageChange', syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener('languageChange', syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, []);

  useEffect(() => {
    const refresh = () => {
      setHasClients((getTopbarClientsCache() || []).length > 0);
      setAssignmentsFlag(readShowAssignments(canSeeCompany));
    };
    refresh();
    window.addEventListener('topbar-clients-updated', refresh);
    return () => window.removeEventListener('topbar-clients-updated', refresh);
  }, [canSeeCompany]);

  const enabled = isAuthenticated;
  const autoOpenEligible = isAuthenticated && location.pathname === DASHBOARD_PATH;

  const steps = useMemo(
    () => buildSteps({
      language,
      hasClients: hasClients || isTourDemoActive(),
      canSeeCompany,
      showAssignments,
    }),
    [language, hasClients, canSeeCompany, showAssignments]
  );

  const welcome = useMemo(
    () => buildWelcome(language),
    [language]
  );

  const {
    isOpen,
    showWelcome,
    welcomeStage,
    currentStep,
    highlightedElement,
    secondaryHighlightedElement,
    targetMissing,
    tooltipKey,
    volume,
    setMascotVolume,
    continueWelcome,
    skipWelcome,
    goNext,
    goPrev,
    goToStepId,
    skipSection,
    canSkipSection,
    skipTour,
  } = usePortfolioPlaybook({ enabled, autoOpenEligible, completionKey: COMPLETION_KEY, steps });

  const preTourRef = useRef({ activated: false, client: null, cache: null });
  const activateTimersRef = useRef([]);

  useEffect(() => () => {
    activateTimersRef.current.forEach((id) => clearTimeout(id));
    activateTimersRef.current = [];
  }, []);

  const scheduleAdvance = useCallback((fn, delay) => {
    const id = window.setTimeout(() => {
      activateTimersRef.current = activateTimersRef.current.filter((t) => t !== id);
      fn();
    }, delay);
    activateTimersRef.current.push(id);
  }, []);

  const pickDemoClient = useCallback(() => {
    if (isTourDemoActive()) {
      return buildDemoClient(getDemoProviderId());
    }
    const list = getTopbarClientsCache() || [];
    let savedId = null;
    try {
      savedId = sessionStorage.getItem(CLIENT_KEY);
    } catch {
      savedId = null;
    }
    if (savedId) {
      const found = list.find((c) => String(c.id) === savedId);
      if (found) return found;
    }
    if (selectedClient) return selectedClient;
    return list[0] || null;
  }, [selectedClient]);

  useEffect(() => {
    if (!isOpen) {
      if (!preTourRef.current.activated) return;
      setTourDemoActive(false);
      setSelectedClient(preTourRef.current.client ?? null);
      setTopbarClientsCache(preTourRef.current.cache ?? []);
      preTourRef.current = { activated: false, client: null, cache: null };
      return;
    }

    if (!currentUser?.id) return;

    const demo = buildDemoClient(currentUser.id);
    const isDemoSelected = !!(
      selectedClient
      && (String(selectedClient.id) === DEMO_CLIENT_ID || selectedClient.user_code === demo.user_code)
    );

    // Already armed: only nudge back to Maya if something switched the client away.
    // Do not rewrite caches / re-dispatch demo-changed — that remounts the table
    // and drops the View-eye highlight mid "Open a client".
    if (preTourRef.current.activated) {
      if (!isDemoSelected) setSelectedClient(demo);
      return;
    }

    const providerId = currentUser.id;
    preTourRef.current = {
      activated: true,
      client: selectedClient,
      cache: [...(getTopbarClientsCache() || [])],
    };

    setTourDemoActive(true, providerId);
    const existing = (getTopbarClientsCache() || []).filter(
      (c) => c?.user_code !== demo.user_code && String(c?.id) !== DEMO_CLIENT_ID
    );
    setTopbarClientsCache([demo, ...existing]);
    setSelectedClient(demo);
    setHasClients(true);
    try {
      sessionStorage.setItem(CLIENT_KEY, String(demo.id));
    } catch {
      // ignore
    }
  }, [isOpen, currentUser?.id, selectedClient, setSelectedClient]);

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Only drive navigation when the STEP changes — not on every re-render. Otherwise
  // a mid-step client change (e.g. clicking View opens Maya) re-runs this effect
  // while currentStep still points at the dashboard step and bounces the user back.
  const lastNavStepRef = useRef(-1);
  useEffect(() => {
    if (!isOpen || showWelcome) {
      lastNavStepRef.current = -1;
      return;
    }
    const step = steps[currentStep];
    if (!step?.route) return;
    if (lastNavStepRef.current === currentStep) return;

    if (pathnameRef.current === step.route) {
      lastNavStepRef.current = currentStep;
      return;
    }

    if (step.route !== DASHBOARD_PATH) {
      const demo = pickDemoClient();
      if (demo) {
        setSelectedClient(demo);
        try {
          sessionStorage.setItem(CLIENT_KEY, String(demo.id));
        } catch {
          // ignore
        }
      }
    }
    navigate(step.route);
    lastNavStepRef.current = currentStep;
  }, [isOpen, showWelcome, currentStep, steps, navigate, setSelectedClient, pickDemoClient]);

  const demoClientName = useMemo(() => {
    const demo = pickDemoClient();
    return demo ? (demo.name || demo.full_name || '') : '';
  }, [pickDemoClient]);

  // Interactive spotlight activate (hit proxy). Open-client must not depend on
  // forwarding a click into a dashboard button under the fixed overlay.
  const handleInteractiveActivate = useCallback(() => {
    const step = steps[currentStep];
    if (!step?.interactive) return;

    if (step.id === 'act-actions') {
      const demo = pickDemoClient() || buildDemoClient(getDemoProviderId() || currentUser?.id);
      if (demo) {
        setSelectedClient(demo);
        try {
          sessionStorage.setItem(CLIENT_KEY, String(demo.id));
        } catch {
          // ignore
        }
      }
      navigate(CLIENT_DASHBOARD_PATH);
      scheduleAdvance(() => {
        if (step.advanceToStepId) goToStepId(step.advanceToStepId);
        else goNext();
      }, 80);
      return;
    }

    // Other interactive steps: click the live control when possible.
    // Prefer clickTarget when the spotlight is a larger shell (e.g. Company menu).
    const isMobile = isMobileViewport();
    const clickId = step.clickTarget
      ? ((isMobile && step.clickTargetMobile) ? step.clickTargetMobile : step.clickTarget)
      : null;
    const clickEl = clickId
      ? findTourElement(clickId, { isMobile })
      : null;
    const el = clickEl || highlightedElement;
    if (el?.isConnected && typeof el.click === 'function') {
      el.click();
      return;
    }
    if (step.advanceToStepId) goToStepId(step.advanceToStepId);
    else goNext();
  }, [
    steps,
    currentStep,
    pickDemoClient,
    currentUser?.id,
    setSelectedClient,
    navigate,
    goToStepId,
    goNext,
    highlightedElement,
    scheduleAdvance,
  ]);

  const toggleLanguage = () => {
    const next = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', next);
    document.documentElement.dir = next === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = next === 'hebrew' ? 'he' : 'en';
    setLanguage(next);
    window.dispatchEvent(new Event('languageChange'));
  };

  // Warm the cache for upcoming clips (and the welcome clip) so steps start instantly.
  const preloadedRef = useRef(new Map());
  useEffect(() => {
    if (!isOpen) return;
    const ids = showWelcome
      ? ['welcome', steps[0]?.id]
      : [steps[currentStep + 1], steps[currentStep + 2]].map((s) => (s && 'videoId' in s ? s.videoId : s?.id));
    ids.filter(Boolean).forEach((id) => {
      const src = getMascotVideoCandidates(id, language)[0];
      if (!src || preloadedRef.current.has(src)) return;
      const v = document.createElement('video');
      v.preload = 'auto';
      v.muted = true;
      v.src = src;
      preloadedRef.current.set(src, v);
    });
  }, [isOpen, showWelcome, currentStep, steps, language]);

  const currentStepDef = steps[currentStep];
  const needsDashboardReady =
    isOpen && !showWelcome && !!currentStepDef?.waitForDashboard;
  const dashboardReady = useAdminDashboardReady(needsDashboardReady);
  const waitingForDashboard = needsDashboardReady && !dashboardReady;

  if (!isAuthenticated || !isOpen) return null;

  return (
    <TourOverlay
      showWelcome={showWelcome}
      welcomeStage={welcomeStage}
      waitingForDashboard={waitingForDashboard}
      step={currentStepDef}
      stepIndex={currentStep}
      totalSteps={steps.length}
      highlightedElement={highlightedElement}
      secondaryHighlightedElement={secondaryHighlightedElement}
      targetMissing={targetMissing}
      tooltipKey={tooltipKey}
      language={language}
      direction={language === 'hebrew' ? 'rtl' : 'ltr'}
      isDarkMode={isDarkMode}
      welcome={welcome}
      demoClientName={demoClientName}
      onStart={continueWelcome}
      onSkipWelcome={skipWelcome}
      onNext={goNext}
      onPrev={goPrev}
      onSkip={skipTour}
      onSkipSection={skipSection}
      canSkipSection={canSkipSection}
      onToggleLanguage={toggleLanguage}
      onInteractiveActivate={handleInteractiveActivate}
      volume={volume}
      onVolumeChange={setMascotVolume}
    />
  );
};

export default PortfolioPlaybookTour;
